import React, { useState } from 'react';
import { useCart } from '../cart/CartContext';
import { useNavigate } from 'react-router-dom';
import dataService from '../data/dataService';
import '../styles/Checkout.css';

type PaymentMethod = 'credit-card' | 'paypal' | 'google-pay';

interface ShippingInfo {
  fullName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
}

const DEFAULT_SHIPPING: ShippingInfo = {
  fullName: 'Alex Johnson',
  address: '742 Evergreen Terrace',
  city: 'Portland',
  state: 'OR',
  zip: '97201',
  phone: '(503) 555-0142',
};

const MOCK_CARD = {
  number: 'XXXX-ABCD-1234-EFGH',
  expiry: '12/29',
  cid: 'A1B',
};

const Checkout: React.FC = () => {
  const { cart, loading, checkout } = useCart();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState<'shipping' | 'payment' | 'review'>('shipping');
  const [shipping, setShipping] = useState<ShippingInfo>(DEFAULT_SHIPPING);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('credit-card');
  const [cardNumber, setCardNumber] = useState(MOCK_CARD.number);
  const [cardExpiry, setCardExpiry] = useState(MOCK_CARD.expiry);
  const [cardCid, setCardCid] = useState(MOCK_CARD.cid);
  const [discountCode, setDiscountCode] = useState('');
  const [discountPercent, setDiscountPercent] = useState<number | null>(null);
  const [discountMsg, setDiscountMsg] = useState('');
  const [discountError, setDiscountError] = useState(false);
  const [applyingDiscount, setApplyingDiscount] = useState(false);

  const handleCheckout = async () => {
    setSubmitting(true);
    setError('');
    const orderId = await checkout();
    setSubmitting(false);
    if (orderId) {
      navigate(`/orders/${orderId}`);
    } else {
      setError('Checkout failed. Please try again.');
    }
  };

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) return;
    setApplyingDiscount(true);
    setDiscountMsg('');
    setDiscountError(false);
    try {
      const result = await dataService.validateDiscountCode(discountCode.trim());
      setDiscountPercent(result.discountPercent);
      setDiscountMsg(`Code "${result.code}" applied — ${result.discountPercent}% off!`);
      setDiscountError(false);
    } catch (err: any) {
      setDiscountPercent(null);
      setDiscountMsg(err.message || 'Invalid discount code.');
      setDiscountError(true);
    }
    setApplyingDiscount(false);
  };

  const subtotal = cart ? cart.total : 0;
  const discountAmount = discountPercent ? subtotal * (discountPercent / 100) : 0;
  const finalTotal = subtotal - discountAmount;

  const shippingValid =
    shipping.fullName.trim() !== '' &&
    shipping.address.trim() !== '' &&
    shipping.city.trim() !== '' &&
    shipping.state.trim() !== '' &&
    shipping.zip.trim() !== '';

  if (loading) {
    return <div className="checkout-page"><p>Loading cart...</p></div>;
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="checkout-page">
        <h1>Checkout</h1>
        <p>Your cart is empty. <a href="/products">Browse products</a></p>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <h1>Checkout</h1>

      {/* Progress Steps */}
      <div className="checkout-steps">
        <div className={`checkout-step ${step === 'shipping' ? 'active' : ''} ${step !== 'shipping' ? 'completed' : ''}`} onClick={() => setStep('shipping')}>
          <span className="step-number">{step !== 'shipping' ? '✓' : '1'}</span>
          <span className="step-label">Shipping</span>
        </div>
        <div className="step-connector" />
        <div className={`checkout-step ${step === 'payment' ? 'active' : ''} ${step === 'review' ? 'completed' : ''}`} onClick={() => step === 'review' ? setStep('payment') : undefined}>
          <span className="step-number">{step === 'review' ? '✓' : '2'}</span>
          <span className="step-label">Payment</span>
        </div>
        <div className="step-connector" />
        <div className={`checkout-step ${step === 'review' ? 'active' : ''}`}>
          <span className="step-number">3</span>
          <span className="step-label">Review</span>
        </div>
      </div>

      {/* Step 1: Shipping */}
      {step === 'shipping' && (
        <div className="checkout-section">
          <h2>📦 Shipping Information</h2>
          <div className="form-grid">
            <div className="form-field full-width">
              <label>Full Name</label>
              <input type="text" value={shipping.fullName} onChange={(e) => setShipping({ ...shipping, fullName: e.target.value })} placeholder="Full Name" />
            </div>
            <div className="form-field full-width">
              <label>Street Address</label>
              <input type="text" value={shipping.address} onChange={(e) => setShipping({ ...shipping, address: e.target.value })} placeholder="Street Address" />
            </div>
            <div className="form-field">
              <label>City</label>
              <input type="text" value={shipping.city} onChange={(e) => setShipping({ ...shipping, city: e.target.value })} placeholder="City" />
            </div>
            <div className="form-field">
              <label>State</label>
              <input type="text" value={shipping.state} onChange={(e) => setShipping({ ...shipping, state: e.target.value })} placeholder="State" maxLength={2} />
            </div>
            <div className="form-field">
              <label>ZIP Code</label>
              <input type="text" value={shipping.zip} onChange={(e) => setShipping({ ...shipping, zip: e.target.value })} placeholder="ZIP Code" maxLength={10} />
            </div>
            <div className="form-field">
              <label>Phone <span className="optional">(optional)</span></label>
              <input type="tel" value={shipping.phone} onChange={(e) => setShipping({ ...shipping, phone: e.target.value })} placeholder="(555) 555-0000" />
            </div>
          </div>
          <button className="checkout-next-btn" disabled={!shippingValid} onClick={() => setStep('payment')}>
            Continue to Payment →
          </button>
        </div>
      )}

      {/* Step 2: Payment */}
      {step === 'payment' && (
        <div className="checkout-section">
          <h2>💳 Payment Method</h2>
          <div className="payment-methods">
            <label className={`payment-option ${paymentMethod === 'credit-card' ? 'selected' : ''}`}>
              <input type="radio" name="payment" value="credit-card" checked={paymentMethod === 'credit-card'} onChange={() => setPaymentMethod('credit-card')} />
              <svg className="payment-icon" viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" />
              </svg>
              <span className="payment-label">Credit Card</span>
              <span className="payment-badge">Default</span>
            </label>
            <label className={`payment-option ${paymentMethod === 'paypal' ? 'selected' : ''}`}>
              <input type="radio" name="payment" value="paypal" checked={paymentMethod === 'paypal'} onChange={() => setPaymentMethod('paypal')} />
              <svg className="payment-icon" viewBox="0 0 24 24" width="28" height="28">
                <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 2.73A.77.77 0 0 1 5.703 2h5.836c2.96 0 5.024 1.587 4.6 4.547-.612 4.26-3.527 5.59-6.872 5.59H7.466l-1.09 6.8a.641.641 0 0 1-.633.54h-.667Z" fill="#003087" />
                <path d="M19.536 6.547C18.924 10.807 16.01 12.137 12.665 12.137h-1.8l-1.29 8.16a.54.54 0 0 0 .534.627h3.16a.67.67 0 0 0 .66-.564l.69-4.34a.67.67 0 0 1 .66-.564h1.52c3.1 0 5.07-2.04 5.56-5.1.34-2.1-.07-3.63-1.22-4.25" fill="#0070E0" />
              </svg>
              <span className="payment-label">PayPal</span>
            </label>
            <label className={`payment-option ${paymentMethod === 'google-pay' ? 'selected' : ''}`}>
              <input type="radio" name="payment" value="google-pay" checked={paymentMethod === 'google-pay'} onChange={() => setPaymentMethod('google-pay')} />
              <svg className="payment-icon" viewBox="0 0 24 24" width="28" height="28">
                <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z" fill="#4285F4" />
              </svg>
              <span className="payment-label">Google Pay</span>
            </label>
          </div>

          {paymentMethod === 'credit-card' && (
            <div className="card-details">
              <p className="mock-notice">🔒 Demo mode — pre-filled with mock data</p>
              <div className="form-grid">
                <div className="form-field full-width">
                  <label>Card Number</label>
                  <input type="text" value={cardNumber} onChange={(e) => setCardNumber(e.target.value)} readOnly className="mock-input" />
                </div>
                <div className="form-field">
                  <label>Expiry</label>
                  <input type="text" value={cardExpiry} onChange={(e) => setCardExpiry(e.target.value)} readOnly className="mock-input" />
                </div>
                <div className="form-field">
                  <label>CID</label>
                  <input type="text" value={cardCid} onChange={(e) => setCardCid(e.target.value)} readOnly className="mock-input" />
                </div>
              </div>
            </div>
          )}

          {paymentMethod === 'paypal' && (
            <div className="alt-payment-notice">
              <svg width="24" height="24" viewBox="0 0 24 24"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 2.73A.77.77 0 0 1 5.703 2h5.836c2.96 0 5.024 1.587 4.6 4.547-.612 4.26-3.527 5.59-6.872 5.59H7.466l-1.09 6.8a.641.641 0 0 1-.633.54h-.667Z" fill="#003087" /><path d="M19.536 6.547C18.924 10.807 16.01 12.137 12.665 12.137h-1.8l-1.29 8.16a.54.54 0 0 0 .534.627h3.16a.67.67 0 0 0 .66-.564l.69-4.34a.67.67 0 0 1 .66-.564h1.52c3.1 0 5.07-2.04 5.56-5.1.34-2.1-.07-3.63-1.22-4.25" fill="#0070E0" /></svg>
              <p>You will be redirected to <strong>PayPal</strong> to complete payment.<br /><span className="mock-tag">Demo — no redirect</span></p>
            </div>
          )}

          {paymentMethod === 'google-pay' && (
            <div className="alt-payment-notice">
              <svg width="24" height="24" viewBox="0 0 24 24"><path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z" fill="#4285F4" /></svg>
              <p>You will be redirected to <strong>Google Pay</strong> to complete payment.<br /><span className="mock-tag">Demo — no redirect</span></p>
            </div>
          )}

          <div className="checkout-btn-row">
            <button className="checkout-back-btn" onClick={() => setStep('shipping')}>← Back</button>
            <button className="checkout-next-btn" onClick={() => setStep('review')}>Review Order →</button>
          </div>
        </div>
      )}

      {/* Step 3: Review & Place Order */}
      {step === 'review' && (
        <div className="checkout-section">
          <h2>📋 Review Your Order</h2>

          <div className="review-block">
            <div className="review-header">
              <h3>Shipping</h3>
              <button className="review-edit-btn" onClick={() => setStep('shipping')}>Edit</button>
            </div>
            <p>{shipping.fullName}<br />{shipping.address}<br />{shipping.city}, {shipping.state} {shipping.zip}{shipping.phone && <><br />{shipping.phone}</>}</p>
          </div>

          <div className="review-block">
            <div className="review-header">
              <h3>Payment</h3>
              <button className="review-edit-btn" onClick={() => setStep('payment')}>Edit</button>
            </div>
            {paymentMethod === 'credit-card' && <p>💳 Credit Card ending in {cardNumber.slice(-4)}</p>}
            {paymentMethod === 'paypal' && <p>PayPal</p>}
            {paymentMethod === 'google-pay' && <p>Google Pay</p>}
          </div>

          <div className="review-block">
            <h3>Items ({cart.items.reduce((s, i) => s + i.quantity, 0)})</h3>
            <table className="checkout-table">
              <thead>
                <tr><th>Product</th><th>Qty</th><th>Price</th><th>Subtotal</th></tr>
              </thead>
              <tbody>
                {cart.items.map(item => (
                  <tr key={item.id}>
                    <td>{item.productName}</td>
                    <td>{item.quantity}</td>
                    <td>${item.unitPrice.toFixed(2)}</td>
                    <td>${item.subtotal.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Discount Code */}
          <div className="review-block">
            <h3>Discount Code</h3>
            <div className="discount-section">
              <div className="form-field">
                <input
                  type="text"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                  placeholder="Enter code (e.g. FUR-XXXXXXXX)"
                  disabled={discountPercent !== null}
                />
              </div>
              {discountPercent === null ? (
                <button className="discount-apply-btn" onClick={handleApplyDiscount} disabled={applyingDiscount || !discountCode.trim()}>
                  {applyingDiscount ? 'Checking...' : 'Apply'}
                </button>
              ) : (
                <button className="discount-apply-btn" onClick={() => { setDiscountPercent(null); setDiscountCode(''); setDiscountMsg(''); }}>
                  Remove
                </button>
              )}
            </div>
            {discountMsg && (
              <div className={`discount-result ${discountError ? 'error' : 'success'}`}>
                {discountMsg}
              </div>
            )}
          </div>

          <div className="checkout-total">
            {discountPercent !== null && (
              <>
                <div className="discount-line"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                <div className="discount-line"><span>Discount ({discountPercent}%)</span><span>-${discountAmount.toFixed(2)}</span></div>
              </>
            )}
            <strong>Total: ${finalTotal.toFixed(2)}</strong>
          </div>

          {error && <p className="checkout-error">{error}</p>}

          <div className="checkout-btn-row">
            <button className="checkout-back-btn" onClick={() => setStep('payment')}>← Back</button>
            <button className="checkout-submit-btn" onClick={handleCheckout} disabled={submitting}>
              {submitting ? 'Placing Order...' : 'Place Order'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checkout;
